import React, { useState } from 'react';
import styled from 'styled-components';
import RadioGroup from '../../components/RadioGroup';
import { isNullOrUndefined } from '../../../shared';
import { ConfirmValueButton } from './FluidValidation';
export function ValidationActions({ validation, configService, isEditing }) {
    if (validation.type === "missing-part") {
        return React.createElement(MissingPartValidation, { validation: validation, configService: configService, isEditing: isEditing });
    }
    else if (validation.type === "retry") {
        return React.createElement(RetryValidation, { validation: validation, configService: configService, isEditing: isEditing });
    }
    else if (validation.type === "confirm") {
        return React.createElement(ConfirmValidation, { validation: validation, configService: configService, isEditing: isEditing });
    }
    return null;
}
;
/**
 * CorrectValidation component - Displays validation for correct data
 */
function CorrectValidation({ validation, configService, isEditing }) {
    let value = configService.getValue(validation.fieldPath);
    let valueNotProvided = isNullOrUndefined(value) || value === '';
    return (React.createElement(ValidationBox, null,
        !valueNotProvided && React.createElement(StatusBadge, { "$variant": "success" }, "Valid"),
        valueNotProvided &&
            (React.createElement(StatusBadge, { "$variant": "neutral" }, "Not provided"))));
}
/**
 * ConfirmValidation component - Displays validation for confirmation
 */
function ConfirmValidation({ validation, configService, isEditing }) {
    let options = validation.choices?.map(({ text, value }) => ({ key: value, value: value, text: text }));
    const [selectedValue, setSelectedValue] = useState('');
    const [originalValue] = useState(configService.getValue(validation.fieldPath)?.toString() || '');
    const handleSelect = (selectedValue) => {
        setSelectedValue(selectedValue);
        const selectedChoice = validation?.choices.find(choice => choice.value === selectedValue);
        if (selectedChoice) {
            configService.setValue(validation.fieldPath, selectedChoice.value);
        }
    };
    const handleSave = () => {
        // Value is already set in configService through handleSelect
        // Just need to exit edit mode - this would be handled by parent
    };
    const handleCancel = () => {
        // Restore original value
        configService.setValue(validation.fieldPath, originalValue);
        setSelectedValue('');
    };
    return (React.createElement(ValidationBox, null,
        validation.question && (React.createElement(StatusMessage, null, validation.question)),
        React.createElement(ConfirmValueButton, { fieldPath: validation.fieldPath, text: "Confirm" }),
        isEditing && options && (React.createElement("div", { style: { marginTop: '0.375rem' } },
            React.createElement(RadioGroup, { onSelect: handleSelect, options: options })))));
}
/**
 * MissingPartValidation component - Displays validation for missing parts
 */
function MissingPartValidation({ validation, configService, isEditing }) {
    let currentValue = configService.getValue(validation.fieldPath)?.toString();
    const [partValues, setPartValues] = useState({});
    const [originalValue] = useState(currentValue);
    const handlePartChange = (part, value) => {
        setPartValues(prev => ({
            ...prev,
            [part]: value
        }));
    };
    const handleSave = () => {
        // Append all the missing parts to the current value
        let updatedValue = originalValue || '';
        Object.entries(partValues).forEach(([part, value]) => {
            if (value.trim()) {
                updatedValue += `, ${part}: ${value}`;
            }
        });
        configService.setValue(validation.fieldPath, updatedValue);
    };
    const handleCancel = () => {
        // Restore original value and clear temp values
        configService.setValue(validation.fieldPath, originalValue);
        setPartValues({});
    };
    return (React.createElement(ValidationBox, null, validation.question && (React.createElement(StatusMessage, null, validation.question))));
}
/**
 * RetryValidation component - Displays validation for retry
 */
function RetryValidation({ validation, configService, isEditing }) {
    return (React.createElement(ValidationBox, null, validation.question && (React.createElement(StatusMessage, { dangerouslySetInnerHTML: { __html: validation.question } }))));
}
/**
 * Styles
 */
const ValidationBox = styled.div.attrs({
    className: 'validation-box'
}) `
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
`;
const StatusBadge = styled.div.attrs({
    className: 'validation-status-badge'
}) `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;                 /* tight */
  padding: 0.15rem 0.4rem;      /* compact */
  border-radius: 0.375rem;      /* rounded-md */
  font-size: 0.72rem;           /* slightly smaller */
  font-weight: 500;
  
  ${props => {
    switch (props.$variant) {
        case 'danger':
            return `
          background: #fee2e2;
          color: #b91c1c;
          &::before {
            content: '●';
            color: #dc2626;
          }
        `;
        case 'success':
            return `
          background: #f0fdf4;
          color: #16a34a;
          &::before {
            content: '●';
            color: #16a34a;
          }
        `;
        case 'warning':
            return `
          background: #fffbeb;
          color:rgb(0, 0, 0);
          &::before {
            content: '●';
            color:rgb(0, 0, 0);
          }
        `;
        case 'neutral':
            return `
          background: #f3f4f6;
          color: #6b7280;
          &::before {
            content: '●';
            color: #6b7280;
          }
        `;
    }
}}
`;
const StatusMessage = styled.p.attrs({
    className: 'validation-status-message f-color-invalid',
}) `
  text-align: left;
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  letter-spacing: 0.04em; 
`;
